import { TimePeriod } from "@flakiness/server/common/timePeriod.js";
import { WireTypes } from "@flakiness/server/common/wireTypes.js";

import { html, nothing, TemplateResult } from "lit";
import { styleMap } from "lit/directives/style-map.js";
import { Temporal } from "temporal-polyfill";
import { CommitRegistry } from "../commitRegistry.js";
import { BarChart } from "../components/bar-chart.js";
import { DataColumn } from "../components/data-table.js";
import { FKPopupDismissed, FKPopupRequested } from "../components/fk-popup.js";

export function createGenericOutcomeColumn<ELEMENT>(options: {
  outcome: WireTypes.Outcome,
  testStats: (el: ELEMENT) => WireTypes.OutcomesCount,
  href?: (el: ELEMENT) => string|undefined,
}): DataColumn<ELEMENT, WireTypes.Outcome> {
  return {
    sortAxis: options.outcome,
    renderHeader() {
      if (options.outcome === 'expected')
        return html`Passed`;
      if (options.outcome === 'unexpected')
        return html`Failed`;
      if (options.outcome === 'flaked')
        return html`Flaked`;
      if (options.outcome === 'regressed')
        return html`New Failures`;
      if (options.outcome === 'skipped')
        return html`Skipped`;
      throw new Error(`Unknown outcome "${options.outcome}"`);
    },
    renderElement(element: ELEMENT): TemplateResult {
      const testStats = options.testStats(element);
      return html`
        <a href=${options.href?.call(null, element) ?? nothing}>
          <int-num .x=${testStats[options.outcome]}></int-num>
        </a>
      `;
    },
  }
}

export function createDurationTrendColumn<ELEMENT extends { durationMs: number, durationChangeMs: number }>() {
  return {
    sortAxis: 'duration_trend' as const,
    setting: { settingId: 'test-stats.duration-trend', title: 'Duration Trend' },
    renderHeader() { return html`
      <h-box style="gap: var(--sl-spacing-2x-small)">
        <span>Duration Trend</span>
        <fk-help style="font-size: var(--sl-font-size-x-small)">
          <p>The change from the oldest bar in the History column to the test's current duration.</p>
          <p>Red means the test got more than 20% slower. Green means it got more than 20% faster.</p>
        </fk-help>
      </h-box>
    `; },
    renderElement({ durationMs, durationChangeMs }: ELEMENT): TemplateResult {
      return html`
        <fk-duration-trend .ms=${durationMs} .baseline=${durationMs - durationChangeMs}></fk-duration-trend>
      `;
    },
  } satisfies DataColumn<ELEMENT, any>;
}

const formatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'short', // 'Sun'
  month: 'short',   // 'Jan'
  day: 'numeric',   // '1'
  year: 'numeric'   // '2023'
});

/**
 * A history bar entry. Every bar says what it covers, so a commit bar (the appended merge commit
 * in the PR view, say) reads as a commit even among period bars.
 */
export type TestHistoryEntry = WireTypes.TestHistory;

/** Where a bar sits: the start of the period it buckets, or the time of its commit. */
function barTime(entry: TestHistoryEntry, timezone: string): Temporal.PlainDateTime {
  return Temporal.Instant.fromEpochMilliseconds(entry.sinceTimestamp).toZonedDateTimeISO(timezone).toPlainDateTime();
}

/** The first and the last day a bar covers. */
function barRange(entry: TestHistoryEntry, timezone: string): { since: Temporal.PlainDate, until: Temporal.PlainDate } {
  const time = barTime(entry, timezone);
  if (entry.historyType === 'commit')
    return { since: time.toPlainDate(), until: time.toPlainDate() };
  return { since: time.toPlainDate(), until: TimePeriod.lastDay(TimePeriod.periodEnd(entry.historyType, time)) };
}

/** The three-hour span a moment falls into, e.g. "Aug 29, 2026, 03:00 – 06:00". */
function formatSpan(time: Temporal.PlainDateTime): string {
  const since = TimePeriod.periodStart('3h', time);
  const until = TimePeriod.periodEnd('3h', time);
  const hour = (time: Temporal.PlainDateTime) => time.toPlainTime().toString({ smallestUnit: 'minute' });
  return `${formatRange(since.toPlainDate(), since.toPlainDate())}, ${hour(since)} – ${hour(until)}`;
}

function formatRange(since: Temporal.PlainDate, until: Temporal.PlainDate): string {
  const monthDay = (day: Temporal.PlainDate) => day.toLocaleString('en-US', { month: 'short', day: 'numeric' });
  if (since.equals(until))
    return `${monthDay(since)}, ${since.year}`;
  const to = since.month === until.month ? String(until.day) : monthDay(until);
  return `${monthDay(since)} – ${to}, ${until.year}`;
}

export type TestHistorySnippetSelection = {
  entry: WireTypes.TestHistory,
  since: Temporal.PlainDate,
  until: Temporal.PlainDate,
  tz: string,
}

export function createTestHistorySnippet(options: {
  timezone: string,
  history: TestHistoryEntry[],
  commitRegistry: CommitRegistry,
  onHistorySelected?: (options: TestHistorySnippetSelection) => void,
}) {
  const cr = options.commitRegistry;
  // The popup anchors to the whole chart. Left-aligning it keeps the card's
  // text in place as its width changes from bar to bar.
  return html`
    <fk-popup placement=top-start distance=10>${BarChart.render({
      data: options.history.map((hEntry, id) => ({
        date: barTime(hEntry, options.timezone).toPlainDate(),
        entry: hEntry,
        outcome: hEntry.outcome as WireTypes.Outcome,
        value: hEntry.duration,
      })).toReversed(),
      linkifier: (datum) => () => {
        // Open the history popup over the range the clicked bar covers, not the newest entry.
        const { since, until } = barRange(datum.entry, options.timezone);
        options.onHistorySelected?.call(null, { entry: datum.entry, since, until, tz: options.timezone });
      },
      hover: (event) => {
        const datum = event.detail.datum;
        const commit = cr.commit(event.detail.datum.entry.commitId);
        const { since, until } = barRange(datum.entry, options.timezone);
        // Distance from today in the project timezone: "Today" for the current
        // (or any future-edge) day, otherwise "N day(s)".
        const daysAgo = Temporal.Now.plainDateISO(options.timezone).since(until, { largestUnit: 'day' }).days;
        const relativeDay = daysAgo <= 0 ? 'Today' : `${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`;
        // A period bar renders its range; a commit bar (e.g. the appended PR merge commit)
        // renders the commit.
        const popover = datum.entry.historyType !== 'commit' ? html`
          <h-box style="padding: var(--sl-spacing-small);">
            <fk-outcome outcome=${datum.outcome}></fk-outcome>
            <span>${datum.entry.historyType === '3h' ? formatSpan(barTime(datum.entry, options.timezone)) : formatRange(since, until)}</span>
            <span style="color: var(--sl-color-neutral-500);">(${relativeDay})</span>
            <span>•</span>
            <time-interval .ms=${datum.entry.duration}></time-interval>
          </h-box>
        ` : html`
          <h-box style="padding: var(--sl-spacing-small);">
            <fk-outcome outcome=${datum.outcome}></fk-outcome>
            <fk-commit .commit=${commit}></fk-commit>
            <span>•</span>
            <time-interval .ms=${datum.entry.duration}></time-interval>
          </h-box>
        `;
        event.currentTarget?.dispatchEvent(new FKPopupRequested(popover, event.detail.bar));
      },
      leave: (event) => {
        event.currentTarget?.dispatchEvent(new FKPopupDismissed());
      },
      style: {
        'border-bottom': '1px solid var(--fk-color-border)',
        'max-width': '120px',
        height: '30px',
      },
    })}</fk-popup>
  `;
}

export function createTestHistoryColumn<ELEMENT>(options: {
  name: string|TemplateResult,
  timezone: string,
  history: (e: ELEMENT) => TestHistoryEntry[],
  commitRegistry: CommitRegistry,
  // Keeps only the bars sitting in `[since, until]`, so one history can be split across columns.
  range?: { since: Temporal.PlainDate, until: Temporal.PlainDate },
  // Lets users hide the column from the table's column settings. Without it, the column is
  // always shown.
  setting?: { settingId: string, title: string },
  onHistorySelected?: (element: ELEMENT, options: TestHistorySnippetSelection) => void,
}) {
  const inRange = (entry: TestHistoryEntry) => {
    if (!options.range)
      return true;
    const day = barTime(entry, options.timezone).toPlainDate();
    return Temporal.PlainDate.compare(options.range.since, day) <= 0 && Temporal.PlainDate.compare(day, options.range.until) <= 0;
  };
  return {
    sortAxis: undefined,
    width: 'auto',
    setting: options.setting,
    renderHeader() { return html`${options.name}`; },
    renderElement(element: ELEMENT): TemplateResult {
      return html`
        <h-box style=${styleMap({
          'flex': '1',
          'justify-content': 'end',
        })
      }>
        ${createTestHistorySnippet({
          history: options.history(element).filter(inRange),
          timezone: options.timezone,
          commitRegistry: options.commitRegistry,
          onHistorySelected: (entry) => {
            options.onHistorySelected?.(element, entry);
          },
        })}
        </h-box>
      `;
    }
   } satisfies DataColumn<ELEMENT, any>;
}
